import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import BasicLayout from '../components/Shared/BasicLayout';
import Constants from '../constants';
import { Navigate, useSearchParams } from '../react-router';
import { UserContext } from '../contexts/user';
import Spinner from '../components/Spinner';
import { canAccessOrgReports, canAccessReports } from '../utils/reportsAccess';

const WeeklyUsageReportTab = React.lazy(
  () => import('../components/Reports/WeeklyUsageReportTab')
);
const ProReportTab = React.lazy(() => import('../components/Reports/ProReportTab'));
const JobQueueTab = React.lazy(() => import('../components/Reports/JobQueueTab'));

type ReportTab = 'weekly' | 'pro' | 'jobQueue';

const TAB_LABELS: Record<ReportTab, string> = {
  weekly: 'Weekly Usage',
  pro: 'PRO Scores',
  jobQueue: 'Job Queue',
};

function tabFromParam(value?: string | null): ReportTab | undefined {
  return value && value in TAB_LABELS ? (value as ReportTab) : undefined;
}

export default function Reports(): JSX.Element {
  const { state } = React.useContext(UserContext);
  const user = state.user;
  const isWpAdmin = !!user?.wpAdmin;
  const isOrgReportsUser = canAccessOrgReports(user);
  const organizationId = user?.organization?.id;

  const availableTabs = useMemo(() => {
    const tabs: ReportTab[] = [];
    if (isWpAdmin) {
      tabs.push('weekly');
    }
    if (isWpAdmin || isOrgReportsUser) {
      tabs.push('pro');
    }
    if (isWpAdmin) {
      tabs.push('jobQueue');
    }
    return tabs;
  }, [isWpAdmin, isOrgReportsUser]);

  const [searchParams] = useSearchParams();
  const requestedTab = tabFromParam(searchParams.get('tab'));

  const [activeTab, setActiveTab] = useState<ReportTab>(
    () => requestedTab ?? (isWpAdmin ? 'weekly' : 'pro')
  );

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0] ?? 'pro');
    }
  }, [activeTab, availableTabs]);

  // The requested tab can arrive before access is known, so re-apply it once
  // the tab list settles.
  useEffect(() => {
    if (requestedTab && availableTabs.includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, availableTabs]);

  const subtitle = useMemo(() => {
    if (activeTab === 'weekly') {
      return 'Weekly usage summary by organization';
    }
    if (activeTab === 'jobQueue') {
      return `Overdue ${Constants.localizations.campaign} steps and text volume across all organizations`;
    }
    return isWpAdmin
      ? 'Patient Reported Outcome scores across all organizations'
      : `Patient Reported Outcome scores for your ${Constants.localizations.leads.toLowerCase()}`;
  }, [activeTab, isWpAdmin]);

  return (
    <BasicLayout active="/reports">
      {!canAccessReports(user) ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <View style={styles.container}>
          <Text style={styles.heading}>Reports</Text>
          {availableTabs.length > 1 && (
            <View style={styles.tabsRow}>
              {availableTabs.map((tab) => (
                <Pressable
                  key={tab}
                  onPress={() => setActiveTab(tab)}
                  style={[styles.tabButton, activeTab === tab && styles.tabButtonActive]}
                >
                  <Text
                    style={[
                      styles.tabButtonText,
                      activeTab === tab && styles.tabButtonTextActive,
                    ]}
                  >
                    {TAB_LABELS[tab]}
                  </Text>
                </Pressable>
              ))}
            </View>
          )}
          <Text style={styles.subtitle}>{subtitle}</Text>

          <Suspense fallback={<Spinner />}>
            {activeTab === 'weekly' ? (
              <WeeklyUsageReportTab />
            ) : activeTab === 'jobQueue' ? (
              <JobQueueTab />
            ) : (
              <ProReportTab
                fixedOrganizationId={isWpAdmin ? undefined : organizationId}
                showOrganizationColumn={isWpAdmin}
                showOrganizationFilter={isWpAdmin}
                showOrganizationsCard={isWpAdmin}
                showExportCsv={isWpAdmin}
              />
            )}
          </Suspense>
        </View>
      )}
    </BasicLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 25,
    gap: 10,
  },
  heading: {
    fontSize: 32,
    fontFamily: 'GothamBold',
    color: Constants.colors.blue,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
    marginBottom: 20,
  },
  tabsRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  tabButton: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: `${Constants.colors.info}55`,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    cursor: 'pointer',
  },
  tabButtonActive: {
    backgroundColor: Constants.colors.blue,
    borderColor: Constants.colors.blue,
  },
  tabButtonText: {
    fontFamily: 'GothamMedium',
    fontSize: 14,
    color: Constants.colors.dark,
  },
  tabButtonTextActive: {
    color: '#fff',
  },
});
